import { useState } from "react";
import { Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { TextareaField, SelectField, MultiSelect } from "@/components/common";
import { cn } from "@/lib/utils";
import AttachmentInput from "./AttachmentInput";

// ── Single Choice ─────────────────────────────────────────────────────────────
const SingleChoice = ({ question, value, onChange }) => (
  <div className="space-y-2.5 mt-6">
    {question.options.map((opt, i) => {
      const selected = value === opt.label;
      return (
        <button
          key={i}
          onClick={() => onChange?.(opt.label)}
          disabled={!onChange}
          className={cn(
            "w-full text-left px-5 py-3.5 rounded-xl border text-sm transition-all duration-150",
            selected
              ? [
                  "border-emerald-500 bg-emerald-50   text-emerald-800 font-medium shadow-sm",
                  "dark:border-emerald-500 dark:bg-emerald-950/50 dark:text-emerald-300",
                ]
              : [
                  "border-gray-200 bg-white   text-gray-700",
                  "hover:border-gray-300 hover:bg-gray-50",
                  "dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300",
                  "dark:hover:border-gray-600 dark:hover:bg-gray-750",
                ],
            !onChange && "cursor-not-allowed opacity-70",
          )}
        >
          {/* ── Radio indicator ── */}
          <span
            className={cn(
              "inline-flex items-center justify-center w-5 h-5 rounded-full border mr-3",
              "text-xs flex-shrink-0 align-middle transition-colors",
              selected
                ? "border-emerald-500 bg-emerald-500 text-white"
                : ["border-gray-300 text-transparent", "dark:border-gray-600"],
            )}
          >
            ✓
          </span>
          {opt.label}
        </button>
      );
    })}
  </div>
);

// ── Multiple Choice ───────────────────────────────────────────────────────────
const MultipleChoice = ({ question, value = [], onChange }) => {
  const toggle = (label) => {
    if (!onChange) return;
    const next = value.includes(label)
      ? value.filter((v) => v !== label)
      : [...value, label];
    onChange(next);
  };

  return (
    <div className="space-y-2.5 mt-6">
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">
        Select all that apply
      </p>

      {question.options.map((opt, i) => {
        const checked = value.includes(opt.label);
        return (
          <button
            key={i}
            onClick={() => toggle(opt.label)}
            disabled={!onChange}
            className={cn(
              "w-full text-left px-5 py-3.5 rounded-xl border text-sm transition-all duration-150",
              checked
                ? [
                    "border-emerald-500 bg-emerald-50   text-emerald-800 font-medium shadow-sm",
                    "dark:border-emerald-500 dark:bg-emerald-950/50 dark:text-emerald-300",
                  ]
                : [
                    "border-gray-200 bg-white   text-gray-700",
                    "hover:border-gray-300 hover:bg-gray-50",
                    "dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300",
                    "dark:hover:border-gray-600 dark:hover:bg-gray-750",
                  ],
              !onChange && "cursor-not-allowed opacity-70",
            )}
          >
            {/* ── Checkbox indicator ── */}
            <span
              className={cn(
                "inline-flex items-center justify-center w-5 h-5 rounded border mr-3",
                "text-xs flex-shrink-0 align-middle transition-colors",
                checked
                  ? "border-emerald-500 bg-emerald-500 text-white"
                  : [
                      "border-gray-300 text-transparent",
                      "dark:border-gray-600",
                    ],
              )}
            >
              ✓
            </span>
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

// ── Scale ─────────────────────────────────────────────────────────────────────
const ScaleQuestion = ({ question, value, onChange }) => {
  const min = question.min ?? question.minValue ?? 1;
  const max = question.max ?? question.maxValue ?? 10;
  const steps = Array.from({ length: max - min + 1 }, (_, i) => min + i);

  return (
    <div className="mt-6">
      <div className="flex gap-1.5 flex-wrap">
        {steps.map((n) => {
          const selected = value === n;
          return (
            <button
              key={n}
              onClick={() => onChange?.(n)}
              disabled={!onChange}
              className={cn(
                "w-10 h-10 rounded-lg border text-sm font-semibold transition-all duration-150",
                selected
                  ? [
                      "border-emerald-500 bg-emerald-500 text-white shadow-sm",
                      // selected looks the same in dark — emerald-500 pops on dark bg
                    ]
                  : [
                      "border-gray-200 bg-white        text-gray-600",
                      "hover:border-emerald-400 hover:text-emerald-600",
                      "dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300",
                      "dark:hover:border-emerald-500   dark:hover:text-emerald-400",
                    ],
                !onChange && "cursor-not-allowed opacity-70",
              )}
            >
              {n}
            </button>
          );
        })}
      </div>

      {(question.minLabel || question.maxLabel) && (
        <div className="flex justify-between mt-2">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {question.minLabel}
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {question.maxLabel}
          </span>
        </div>
      )}
    </div>
  );
};

// ── Rating ────────────────────────────────────────────────────────────────────
const RatingQuestion = ({ question, value, onChange }) => {
  const [hover, setHover] = useState(null);
  const max = question.max ?? question.maxValue ?? 5;

  return (
    <div className="mt-6 flex gap-2">
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => {
        const filled = n <= (hover ?? value ?? 0);
        return (
          <button
            key={n}
            onMouseEnter={() => onChange && setHover(n)}
            onMouseLeave={() => onChange && setHover(null)}
            onClick={() => onChange?.(n)}
            disabled={!onChange}
            className={!onChange ? "cursor-not-allowed" : ""}
          >
            <Star
              className={cn(
                "h-8 w-8 transition-colors",
                filled
                  ? "fill-amber-400 text-amber-400"
                  : "text-gray-300 dark:text-gray-600",
              )}
            />
          </button>
        );
      })}
    </div>
  );
};

// ── Reusable Two-Option Button Group ─────────────────────────────────────────
const TwoOptionGroup = ({ options, value, onChange }) => (
  <div className="flex gap-3 mt-6">
    {options.map((opt) => {
      const selected = value === opt;
      return (
        <button
          key={opt}
          onClick={() => onChange?.(opt)}
          disabled={!onChange}
          className={cn(
            "flex-1 py-4 rounded-xl border text-sm font-semibold transition-all duration-150",
            selected
              ? [
                  "border-emerald-500 bg-emerald-50   text-emerald-700",
                  "dark:border-emerald-500 dark:bg-emerald-950/50 dark:text-emerald-400",
                  "shadow-sm",
                ]
              : [
                  "border-gray-200 bg-white      text-gray-600",
                  "hover:border-gray-300 hover:bg-gray-50",
                  "dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300",
                  "dark:hover:border-gray-600   dark:hover:bg-gray-750",
                ],
            !onChange && "cursor-not-allowed opacity-70",
          )}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

// ── Yes / No ──────────────────────────────────────────────────────────────────
const YesNo = ({ value, onChange }) => (
  <TwoOptionGroup options={["Yes", "No"]} value={value} onChange={onChange} />
);

// ── True / False ──────────────────────────────────────────────────────────────
const TrueFalse = ({ value, onChange }) => (
  <TwoOptionGroup
    options={["True", "False"]}
    value={value}
    onChange={onChange}
  />
);

// ── Short Text ────────────────────────────────────────────────────────────────
const ShortText = ({ question, value, onChange }) => (
  <Input
    type="text"
    value={value ?? ""}
    onChange={(e) => onChange?.(e.target.value)}
    placeholder={question.placeholder || "Type your answer…"}
    disabled={!onChange}
    className="w-full mt-3"
  />
);

// ── Long Text ─────────────────────────────────────────────────────────────────
const LongText = ({ question, value, onChange }) => (
  <TextareaField
    value={value ?? ""}
    onChange={(e) => onChange?.(e.target.value)}
    placeholder={question.placeholder || "Share your thoughts…"}
    disabled={!onChange}
    rows={question.textareaRows ?? 5}
  />
);

// ── Number ────────────────────────────────────────────────────────────────────
const NumberInput = ({ question, value, onChange }) => (
  <Input
    type="number"
    value={value ?? ""}
    onChange={(e) =>
      onChange?.(e.target.value === "" ? "" : Number(e.target.value))
    }
    placeholder={question.placeholder || "Enter a number…"}
    disabled={!onChange}
    min={question.min}
    max={question.max}
    step={question.step ?? 1}
    className="w-full mt-3"
  />
);

// ── Email ─────────────────────────────────────────────────────────────────────
const EmailInput = ({ question, value, onChange }) => {
  const [touched, setTouched] = useState(false);
  const isValid = !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  return (
    <div className="mt-3">
      <Input
        type="email"
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={() => setTouched(true)}
        placeholder={question.placeholder || "e.g. you@company.com"}
        disabled={!onChange}
        className={cn(
          "w-full",
          touched && !isValid && "border-red-400 focus:ring-red-400",
        )}
      />
      {touched && !isValid && (
        <p className="mt-1.5 text-xs text-red-500">
          Please enter a valid email address.
        </p>
      )}
    </div>
  );
};
const CustomDropdownInput = ({ question, value, onChange }) => {
  const options = (question.options || []).map((opt) => ({
    value: opt.label ?? opt,
    label: opt.label ?? opt,
  }));

  //find selected option to get its description
  const selectedOption = (question.options || []).find(
    (opt) => (opt.label ?? opt) === value,
  );
  const hasDescription = !!selectedOption?.description;

  return (
    <>
      <SelectField
        className="w-full mt-3"
        value={value ?? ""}
        onValueChange={(val) => onChange?.(val)}
        options={options}
        placeholder={question.placeholder || "Select options..."}
        disabled={!onChange}
      />

      {/*show description if selected option has one */}
      {value && hasDescription && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 dark:border-blue-900/40 dark:bg-blue-950/30">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <span className="font-semibold">{value}</span>
            {" — "}
            {selectedOption.description}
          </p>
        </div>
      )}
    </>
  );
};

const DropdownInput = ({ question, value, onChange }) => {
  const options = (question.options || []).map((opt) => ({
    value: opt.label ?? opt,
    label: opt.label ?? opt,
  }));

  return (
    <SelectField
      className="w-full mt-3"
      value={value ?? ""}
      onValueChange={(val) => onChange?.(val)} // ← SelectField uses onValueChange
      options={options}
      placeholder={question.placeholder || "Select options..."}
      disabled={!onChange}
    />
  );
};

const MultiDropdownInput = ({ question, value, onChange }) => {
  const safeValue = Array.isArray(value) ? [...value] : [];

  const options = (question.options || []).map((opt) => ({
    value: opt.label ?? opt,
    label: opt.label ?? opt,
  }));

  const handleChange = (val) => {
    onChange?.([...val]);
  };

  return (
    <div className="space-y-2 mt-3">
      <MultiSelect
        className="w-full"
        options={options}
        value={safeValue}
        onChange={handleChange} // ✅ Fixed: onValueChange → onChange
        placeholder={question.placeholder || "Select options..."}
        maxDisplay={4}
        disabled={!onChange}
      />
      {safeValue.length > 0 && (
        <p className="text-sm text-muted-foreground">
          Selected ({safeValue.length}):{" "}
          {safeValue
            .map((v) => options.find((opt) => opt.value === v)?.label)
            .filter(Boolean)
            .join(", ")}
        </p>
      )}
    </div>
  );
};

const QuestionInput = ({
  question,
  value,
  onChange = null,
  uploadFn = null,
  deleteFn = null,
  downloadFn = null,
  uploadCompleteFn = null,
}) => {
  switch (question.questionType) {
    case "single_choice":
      return (
        <SingleChoice question={question} value={value} onChange={onChange} />
      );
    case "multiple_choice":
      return (
        <MultipleChoice question={question} value={value} onChange={onChange} />
      );
    case "scale":
      return (
        <ScaleQuestion question={question} value={value} onChange={onChange} />
      );
    case "rating":
      return (
        <RatingQuestion question={question} value={value} onChange={onChange} />
      );
    case "yes_no":
      return <YesNo value={value} onChange={onChange} />;
    case "true_false":
      return <TrueFalse value={value} onChange={onChange} />;
    case "short_text":
    case "text":
      return (
        <ShortText question={question} value={value} onChange={onChange} />
      );
    case "long_text":
    case "textarea":
      return <LongText question={question} value={value} onChange={onChange} />;
    case "number":
      return (
        <NumberInput question={question} value={value} onChange={onChange} />
      );
    case "email":
      return (
        <EmailInput question={question} value={value} onChange={onChange} />
      );
    case "dropdown":
      return (
        <DropdownInput question={question} value={value} onChange={onChange} />
      );
    case "custom_dropdown":
      return (
        <CustomDropdownInput
          question={question}
          value={value}
          onChange={onChange}
        />
      );
    case "multiple_dropdown":
      return (
        <MultiDropdownInput
          question={question}
          value={value}
          onChange={onChange}
        />
      );
    case "attachment":
      return (
        <AttachmentInput
          question={question}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          onUpload={uploadFn}
          onDelete={deleteFn}
          onDownload={downloadFn}
          onUploadComplete={uploadCompleteFn}
        />
      );
    default:
      return (
        <ShortText question={question} value={value} onChange={onChange} />
      );
  }
};

export default QuestionInput;
